"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";

import {
  ArrowRight,
  Code,
  Brain,
  Smartphone,
  LayoutDashboard,
  ShoppingCart,
  MessageSquare,
} from "lucide-react";

// import { useBooking } from "@/context/BookingContext";
// import PageHeader from "@/components/PageHeader";

// Images
import customImg from "@/public/images/services/custom.png";
import aiImg from "@/public/images/services/ai.png";
import webImg from "@/public/images/services/web.png";
import cmsImg from "@/public/images/services/cms.png";
import ecomImg from "@/public/images/services/ecom.png";
import consultImg from "@/public/images/services/consult.png";
import PageHeader from "./pageHeader";


export default function ServiceCards() {
//   const { openBookingModal, openContactModal } = useBooking();

  const services = [
    {
      id: "custom-software",
      title: "Custom Software Development",
      description:
        "Custom-built software systems designed around your workflows, users, and long-term business logic.",
      chips: ["Scalable architecture", "Secure APIs", "Integrations"],
      href: "/service/custom-software",
      icon: Code,
      image: customImg,
      gradient: "from-[#7A6FF0] via-[#4DA3FF] to-[#7A6FF0]",
      blob: "#7A6FF0",
    },
    {
      id: "ai-solutions",
      title: "AI Solutions & Automation",
      description:
        "Practical AI embedded into real systems to reduce manual work, improve accuracy, and scale operations.",
      chips: [
        "Workflow automation",
        "Intelligent processing",
        "AI assistants",
      ],
      href: "/service/ai-solutions",
      icon: Brain,
      image: aiImg,
      gradient: "from-[#FF5CAA] via-[#7A6FF0] to-[#FF5CAA]",
      blob: "#FF5CAA",
    },
    {
      id: "web-mobile",
      title: "Web & Mobile App Development",
      description:
        "High-performance web and mobile applications built for usability, scalability, and long-term maintenance.",
      chips: ["Web apps", "Mobile (iOS + Android)", "Performance"],
      href: "/service/web-mobile",
      icon: Smartphone,
      image: webImg,
      gradient: "from-[#4DA3FF] via-[#3DE6C6] to-[#4DA3FF]",
      blob: "#4DA3FF",
    },
    {
      id: "dashboards-cms",
      title: "CMS & Business Systems",
      description:
        "Custom CMS, dashboards, admin panels, and internal tools that give teams clarity and control.",
      chips: [
        "Role-based access",
        "Analytics modules",
        "Internal tools",
      ],
      href: "/service/dashboards-cms",
      icon: LayoutDashboard,
      image: cmsImg,
      gradient: "from-[#F2A65A] via-[#4DA3FF] to-[#F2A65A]",
      blob: "#F2A65A",
    },
    {
      id: "ecommerce-pos",
      title: "E-commerce & POS Systems",
      description:
        "Integrated commerce and point-of-sale systems connecting sales, inventory, and operations at scale.",
      chips: [
        "Inventory management",
        "Payment integration",
        "Omnichannel",
      ],
      href: "/service/ecommerce-pos",
      icon: ShoppingCart,
      image: ecomImg,
      gradient: "from-[#F2A65A] via-[#FF5CAA] to-[#F2A65A]",
      blob: "#F2A65A",
    },
    {
      id: "consulting",
      title: "Consultation & Product Strategy",
      description:
        "Architecture planning, system design, and technical roadmaps to guide critical product decisions early.",
      chips: [
        "Architecture analysis",
        "Tech stack guidance",
        "Scaling strategy",
      ],
      href: "/service/consulting",
      icon: MessageSquare,
      image: consultImg,
      gradient: "from-[#7A6FF0] via-[#F2A65A] to-[#7A6FF0]",
      blob: "#7A6FF0",
    },
  ];

  return (
    <div className="section-clean bg-[#F7F7F7]">
        <div className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle_at_30%_30%,#7A6FF0,transparent_60%)] opacity-20 blur-3xl" />
        <div className="pointer-events-none absolute -top-20 right-[-180px] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle_at_30%_30%,#4DA3FF,transparent_60%)] opacity-[0.18] blur-3xl" />
        <div className="pointer-events-none absolute bottom-[-220px] left-[20%] h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle_at_30%_30%,#FF5CAA,transparent_60%)] opacity-[0.12] blur-3xl" />

      <PageHeader
        label="Our Services"
        title="Software Development Services for SaaS, AI, and Digital Products"
        description="ZySoftec helps startups and growing businesses design, build, and scale software products. From SaaS platforms and AI-powered applications to product design and custom software development, we deliver reliable solutions built for long-term growth."
      />
       <section className="relative overflow-hidden ">
       
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20 lg:pb-24">
         

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Link href={service.href} className="group relative block rounded-2xl p-[1px] h-full">
                    {/* Gradient border */}
                    <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${service.gradient} opacity-30 blur-[0.5px] transition-opacity duration-300 group-hover:opacity-60`} />

                    {/* Card content */}
                    <div className="relative h-full rounded-2xl bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-sm transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-xl overflow-hidden">
                      {/* Image header */}
                      <div className="relative h-40 sm:h-48 overflow-hidden">
                        <div
                          className="absolute inset-0 opacity-20"
                          style={{ background: `linear-gradient(135deg, ${service.blob}30, transparent 60%)` }}
                        />
                        <Image
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        {/* Gradient overlay at bottom */}
                        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/90 to-transparent" />
                      </div>

                      <div className="relative p-6 sm:p-7 pt-4">
                        {/* Inner glow blob */}
                        <div
                          className="pointer-events-none absolute -top-10 -right-10 h-44 w-44 rounded-full opacity-20 blur-2xl transition-transform duration-500 group-hover:translate-x-2 group-hover:-translate-y-2"
                          style={{ background: `radial-gradient(circle at 30% 30%, ${service.blob}, transparent 60%)` }}
                        />

                        {/* Icon badge */}
                        <div
                          className="absolute -top-6 left-6 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110"
                          style={{ backgroundColor: service.blob }}
                        >
                          <Icon className="w-6 h-6 text-white" />
                        </div>

                        {/* Title */}
                        <h3 className="text-lg sm:text-xl font-semibold text-[#0B0F19] mb-2 mt-4">
                          {service.title}
                        </h3>

                        {/* Description */}
                        <p className="text-sm sm:text-[15px] leading-relaxed text-[#4B5563] mb-4">
                          {service.description}
                        </p>

                        {/* Chips */}
                        <div className="flex flex-wrap gap-2 mb-5">
                          {service.chips.map((chip) => (
                            <span
                              key={chip}
                              className="rounded-full bg-black/[0.04] px-3 py-1 text-xs text-[#374151]"
                            >
                              {chip}
                            </span>
                          ))}
                        </div>

                        {/* CTA */}
                        <div className="flex items-center gap-2 text-sm font-medium text-[#0B0F19] group-hover:text-[#7A6FF0] transition-colors">
                          Learn more
                          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      <section className=" bg-[#F7F7F7] py-12 sm:py-16">
        <div className="mx-auto bg-white rounded-xl max-w-6xl px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="card-elevated p-6 sm:p-8 md:flex md:items-center md:justify-between"
          >
            <div className="text-center md:text-left">
              <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-[hsl(var(--text))]">
                Proof, not promises.
              </h3>
              <p className="mt-2 text-sm text-[hsl(var(--subtext))]">
                Explore outcomes from real builds across SaaS, AI, analytics, and operational systems.
              </p>
            </div>
            <Link
              href="/case-studies"
              className="mt-5 md:mt-0 w-full md:w-auto inline-flex items-center justify-center rounded-full bg-[#0B0F19] px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#1a1f2e] hover:shadow-lg"
            >
              View Case Studies
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>
       {/* Global Service CTA */}
       <section
        className="py-12 sm:py-16 lg:py-20 relative overflow-hidden"
        style={{
          background: `
            radial-gradient(500px 200px at 30% 0%, rgba(242,166,90,0.35), transparent 60%),
            radial-gradient(500px 200px at 70% 100%, rgba(122,111,240,0.35), transparent 60%),
            #0B0B0F
          `
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:gap-12 lg:items-start">
            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.32 }}
              className="lg:w-[58%] mb-8 lg:mb-0"
            >
              <h2 className="text-2xl sm:text-[28px] md:text-[34px] lg:text-[40px] text-white mb-4" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
                Ready to Build a System That Scales?
              </h2>
              <p className="text-base sm:text-[17px] text-white/70 mb-4 sm:mb-6" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                Whether you're starting from scratch or improving an existing product, we can help you build software that supports your growth.
              </p>
              <p className="text-sm sm:text-[15px] text-white/60" style={{ fontWeight: 400, lineHeight: 1.7 }}>
                Every engagement starts with a conversation. We'll discuss your goals, constraints, and what success looks like for your project.
              </p>
            </motion.div>

            {/* Right Column - CTA Panel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.32, delay: 0.1 }}
              className="lg:w-[42%]"
            >
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 sm:p-8">
                <p className="text-[11px] sm:text-[12px] uppercase tracking-wide text-[#7A6FF0] mb-4 sm:mb-6 text-center" style={{ fontWeight: 500 }}>
                  Next Step
                </p>
                <p className="text-sm sm:text-[14px] text-white/70 mb-4 sm:mb-6 text-center" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                  Schedule a free consultation to discuss your project requirements.
                </p>

                <motion.button
                //   onClick={openBookingModal}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.16 }}
                  className="w-full inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm sm:text-[15px] font-semibold text-[#0B0B0F] transition-all duration-200 hover:bg-white/90 hover:shadow-lg hover:shadow-white/10 mb-4"
                >
                  Book a Free Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </motion.button>

                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <button
                    // onClick={openContactModal}
                    className="text-sm sm:text-[14px] text-white/70 hover:text-white transition-colors"
                    style={{ fontWeight: 500 }}
                  >
                    Contact Us
                  </button>
                  <span className="text-white/30">·</span>
                  <a
                    href="mailto:hello@zysoftec.com"
                    className="text-sm sm:text-[14px] text-white/70 hover:text-white transition-colors"
                    style={{ fontWeight: 500 }}
                  >
                    Email Us
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}